'use client';
/**
 * LE RELEVÉ MENSUEL DES NON-CONFORMITÉS adressé à un correspondant — depuis sa fiche de l'ANNUAIRE.
 *
 * Repris tel quel de l'ancien écran du carnet de contacts (supprimé le 20/10/2026, Ines : « supprimer totalement
 * le carnet et refaire les liens »). Demande d'origine (RQ, 07/10/2026) : « générer la liste des NC du mois pour ce
 * correspondant et la lui envoyer », puis « simplement pouvoir imprimer un pdf ».
 *
 *  · UN RELEVÉ TÉLÉCHARGÉ N'EST PAS UN RELEVÉ ENVOYÉ : « Télécharger le PDF » et « Envoyer par courriel » sont deux
 *    gestes nommés ; le registre des relevés dit lequel a eu lieu.
 *  · UN ENVOI QUI NE PARTIRA PAS N'EST PAS PROPOSÉ : bouton grisé, et la raison dite (aucune adresse « qualité »,
 *    aucun serveur de courriel).
 *  · UN RELEVÉ VIDE N'EST PAS UNE ERREUR : « aucune non-conformité ne lui est imputée sur ce mois » est un résultat.
 *  · Mois, période ou sélection : la sélection ne propose que les NC de CE correspondant (le serveur refuse les autres).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { I, card, EmptyState, FieldError, useBusy, Help, useConfirmation, Pastille } from '@/components/ui';
import { lbl } from '../ressources/commun';
import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { Tableau } from '@/shared/Tableau';
import { bornesStats, periodePourServeur, moisEnClair, type PeriodeStats } from '@/lib/stats-nc';

/** Le correspondant tel que le serveur le sert (`GET ged/correspondants?entite=…`) : une fiche de l'annuaire. */
export type Ligne = {
  documentId: string; code: string; nom: string; actif: boolean; familles: string[]; manques: string[];
  destinatairesQualite: { adresses: string[]; usage: string | null; repli: boolean };
  compte: { ncs: number; dossiers: number; ouvertes: number; signalees?: number; reclamations?: number };
};
type LigneReleve = {
  ncRef: string; ncTitre: string; date: string; gravite: string; statut: string;
  dossier: string; suites: string;
};
type ModePeriode = 'mois' | 'trimestre' | 'annee' | 'plage' | 'selection';
/** Les statistiques que le serveur calcule pour le correspondant (`statistiquesReleve`) — et que son document reprend. */
type StatsReleve = {
  parNature: { libelle: string; ncs: number }[]; parGravite: { libelle: string; ncs: number }[];
  enCours: number; cloturees: number; evolution: { mois: string; ncs: number; dossiers: number }[];
  precedente: { libelle: string; du: string; au: string; ncs: number; dossiers: number };
};
type Releve = {
  correspondant: { code: string; nom: string };
  mois: string;
  periode: { mode: ModePeriode; du: string; au: string; ncRefs: string[]; libelle: string; cle: string };
  lignes: LigneReleve[];
  compte: {
    ncs: number; dossiers: number; parGravite: Record<string, number>;
    moisPrecedents: { mois: string; ncs: number; dossiers: number }[];
  };
  stats: StatsReleve | null;
};
type NcImputee = { ref: string; titre: string; date: string; gravite: string; statut: string; dossiers: number };
type DossierReleve = {
  releve: Releve;
  correspondant: Ligne;
  moisLabel: string;
  titre: string;
  phraseVide: string;
  /** Les NC imputées à ce correspondant — ce qu'on peut cocher en mode sélection. */
  imputees: NcImputee[];
  anterieurs: {
    ref: string; mois: string; periode: string; mode: ModePeriode; canal: string; envoye: boolean;
    datePrepare: string; dateEnvoi: string; destinataires: string[]; dossiers: number | null;
  }[];
  /* La forme EXACTE de `etatCourriel` (`backend/src/courriel-etat.ts`) :
     `configure` et `consequence`. Les renommer ici au jugé a eu un effet
     silencieux et grave, corrigé le 07/10/2026 avant mise en service :
     `pret !== false` vaut VRAI sur un champ absent, donc le bouton d'envoi
     restait actif alors qu'aucun serveur n'est réglé. Un bouton qui promet un
     envoi impossible est précisément le défaut que tout le lot des
     notifications existe pour fermer. */
  courriel: { configure: boolean; consequence: string; manques: string[] };
  /** Ce qui a DÉJÀ été adressé pour cette période précise — l'avertissement se
   *  lit AVANT le clic, pas après (arbitrage du 08/10/2026). */
  dejaAdresse: { existe: boolean; phrase: string };
  peutEcrire: boolean;
};

const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};

const AIDE_PERIODE = 'Toute la période : les statistiques et toutes les non-conformités de la période choisie en '
  + 'haut de l’onglet. Une sélection : seulement les non-conformités que vous cochez, avec tous leurs dossiers, quelle '
  + 'que soit leur date (sans statistiques).';

/** Une tuile chiffrée. */
const tuile = (valeur: React.ReactNode, libelle: string) => (
  <div style={{ ...card, padding: '10px 12px', display: 'grid', gap: 2 }}>
    <strong style={{ fontSize: 20, color: 'var(--text-strong)', fontFamily: 'var(--font-mono)' }}>{valeur}</strong>
    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{libelle}</span>
  </div>
);
/** Une barre proportionnelle, sur l'échelle `max`. */
export function Barre({ libelle, n, max, suffixe }: { libelle: string; n: number; max: number; suffixe?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}>
      <span style={{ width: 170, fontSize: 12.5, color: 'var(--text-body)', textAlign: 'end', flex: 'none' }}>{libelle}</span>
      <span style={{ flex: 1, height: 14, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${(n / Math.max(1, max)) * 100}%`, height: '100%', background: 'var(--cyan-600)' }} />
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', minWidth: 60 }}>{n}{suffixe || ''}</span>
    </div>
  );
}
export const ecartEnClair = (n: number, avant: number) => (n === avant ? '=' : n > avant ? `+${n - avant}` : `−${avant - n}`);

/** Les statistiques d'un correspondant, telles que son document les imprimera. */
function StatsDuReleve({ r }: { r: Releve }) {
  const st = r.stats;
  if (!st) return null;
  const maxEvo = Math.max(1, ...st.evolution.map((m) => m.ncs));
  const maxRep = Math.max(1, ...st.parNature.map((x) => x.ncs), ...st.parGravite.map((x) => x.ncs));
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {tuile(r.compte.ncs, `non-conformité(s) · ${r.compte.dossiers} dossier(s)`)}
        {tuile(ecartEnClair(r.compte.ncs, st.precedente.ncs), `par rapport à ${st.precedente.libelle} (${st.precedente.ncs})`)}
        {tuile(st.enCours, 'en cours de traitement')}
        {tuile(st.cloturees, 'clôturée(s)')}
      </div>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div><div style={{ ...lbl, marginBottom: 4 }}>Par nature</div>{st.parNature.length ? st.parNature.map((x) => <Barre key={x.libelle} libelle={x.libelle} n={x.ncs} max={maxRep} />) : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>—</span>}</div>
        <div><div style={{ ...lbl, marginBottom: 4 }}>Par gravité</div>{st.parGravite.length ? st.parGravite.map((x) => <Barre key={x.libelle} libelle={x.libelle} n={x.ncs} max={maxRep} />) : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>—</span>}</div>
      </div>
      <div><div style={{ ...lbl, marginBottom: 4 }}>Évolution mois par mois</div>
        {st.evolution.map((m) => <Barre key={m.mois} libelle={moisEnClair(m.mois)} n={m.ncs} max={maxEvo} suffixe={` NC · ${m.dossiers} doss.`} />)}</div>
    </div>
  );
}

/**
 * LES STATISTIQUES ET LE RELEVÉ D'UN CORRESPONDANT, sur la période choisie en haut de l'onglet « Statistiques »
 * (20/10/2026). Ce qu'on voit est ce qui part : le document porte les statistiques puis le détail des NC.
 */
export function PanneauReleve({ ligne, periode, onFerme }: {
  ligne: Ligne; periode: PeriodeStats; onFerme?: () => void;
}) {
  const { showToast } = useGed();
  const [selection, setSelection] = useState(false);
  const mode: ModePeriode = selection ? 'selection' : periode.mode;
  const [choix, setChoix] = useState<string[]>([]);
  const [d, setD] = useState<DossierReleve | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState('');
  const [tour, setTour] = useState(0);
  const { run, estOccupe } = useBusy();
  /* ⚠️ `{confirmation}` DOIT être rendu (voir plus bas), sinon `demander`
     attend une réponse que rien n'affiche et le geste reste suspendu. */
  const { demander, confirmation } = useConfirmation();

  /* Remise à l'état « en chargement » PENDANT LE RENDU, et non dans l'effet :
     un `setState` dans le corps d'un effet déclenche un rendu en cascade
     (React 19), et la règle est une ERREUR de lint dans ce dépôt. Changer de
     mois doit effacer le relevé précédent — sinon on lit octobre sous le titre
     de septembre pendant le temps de l'appel. */
  /* La période telle que le serveur la lit — la même pour l'aperçu, le CSV,
     le PDF et le courriel : un seul objet, sinon l'aperçu et le document
     pourraient ne pas couvrir la même chose. */
  const corpsPeriode: Record<string, unknown> = selection ? { mode: 'selection', ncRefs: choix } : periodePourServeur(periode);
  const requete = new URLSearchParams(selection ? { mode: 'selection', ncRefs: choix.join(',') } : periodePourServeur(periode)).toString();
  /* Une période qui ne tient pas ne part même pas au serveur : il la refuserait, mais l'écran peut le dire tout de suite. */
  const plageInvalide = !selection && !bornesStats(periode);
  const selectionVide = mode === 'selection' && choix.length === 0;

  const cle = `${ligne.documentId}|${requete}|${tour}`;
  const [vue, setVue] = useState(cle);
  if (vue !== cle) { setVue(cle); setD(null); setChargement(!plageInvalide); setEchec(''); }

  useEffect(() => {
    let vivant = true;
    if (plageInvalide) return () => { vivant = false; };
    void fetch(`/api/ged/ged/correspondants/${ligne.documentId}/releve?${requete}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const j = await r.json().catch(() => null);
        throw new Error(j?.error?.message || '');
      })
      .then((j) => { if (vivant) { setD(j); if (!j) setEchec('Le relevé n’a pas pu être calculé.'); } })
      .catch((e: Error) => { if (vivant) setEchec(e.message || 'Le relevé n’a pas pu être calculé.'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [ligne.documentId, requete, tour, plageInvalide]);

  const r = d?.releve;
  const dest = ligne.destinatairesQualite;
  const courrielPret = d?.courriel?.configure === true;
  /* Le bouton grisé DIT pourquoi : c'est exactement ce qui manquait aux deux
     flux publics avant le lot des notifications — on cliquait, l'écran
     remerciait, et rien ne partait. */
  /* Produire et adresser le relevé : le RQ et le CQ (le serveur fait foi). Un
     correspondant désactivé n'en reçoit plus (14/10/2026). */
  const peutAdresser = d?.peutEcrire === true;
  const desactive = ligne.actif === false;
  const empechement = desactive
    ? 'Ce correspondant est désactivé : on ne lui adresse plus de relevé. Réactivez-le d’abord si c’est voulu.'
    : !dest.adresses.length
    ? 'Ce correspondant n’a aucune adresse de courriel : renseignez-en une dans sa fiche, ou téléchargez le PDF.'
    : !courrielPret
      ? `Aucun serveur d’envoi n’est réglé — ${d?.courriel?.consequence || 'rien ne partira'}. Le PDF reste disponible.`
      : '';

  /**
   * Un geste d'écriture, qui ACCEPTE d'être renvoyé une seconde fois.
   *
   * Le serveur répond 409 quand un relevé existe déjà pour cette période : on
   * pose alors la question en nommant ce qui est déjà parti, et l'on rejoue le
   * même appel avec `confirmerSecond`. Le drapeau porte sur CE geste-ci, jamais
   * sur un réglage durable — « ne plus me prévenir » aurait éteint
   * l'avertissement aussi pour les doublons qui sont des fautes.
   */
  const ecrire = async (chemin: string) => {
    const appel = (second: boolean) => fetchEcriture(
      `/api/ged/ged/correspondants/${ligne.documentId}/releve/${chemin}`,
      {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(second ? { ...corpsPeriode, confirmerSecond: true } : corpsPeriode),
      },
    );
    const rep = await appel(false);
    if (rep.status !== 409) return rep;
    const j = await rep.json().catch(() => null);
    const ok = await demander(j?.error?.message || 'Un relevé a déjà été adressé pour cette période. En adresser un second ?',
      { titre: 'Relevé déjà adressé', confirmer: 'Adresser quand même' });
    if (!ok) return null;
    return appel(true);
  };

  const telecharger = () => run('pdf', async () => {
    const rep = await ecrire('pdf');
    if (!rep) return;
    if (!rep.ok) {
      const j = await rep.json().catch(() => null);
      showToast(j?.error?.message
        || 'Le PDF n’a pas pu être produit. Le relevé reste consultable ici et exportable en CSV.', 'erreur');
      return;
    }
    const blob = await rep.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `statistiques-nc-${ligne.code}-${d?.releve.periode.cle || 'periode'}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('Relevé produit — il est enregistré au registre des relevés, et les non-conformités portent la trace.', 'info');
    setTour((t) => t + 1);
  });

  const envoyer = () => run('mail', async () => {
    const rep = await ecrire('envoyer');
    if (!rep) return;
    const j = await rep.json().catch(() => null);
    if (!rep.ok) { showToast(j?.error?.message || 'L’envoi n’a pas abouti.', 'erreur'); return; }
    /* Les DEUX faits : ce qui est parti et ce qui ne l'est pas. Un message qui
       ne dirait que le succès laisserait un échec partiel invisible. */
    const parts = [
      j?.envoyes?.length ? `Envoyé à ${j.envoyes.join(', ')}.` : 'Aucun envoi n’a abouti.',
      j?.echecs?.length ? `Refusé pour ${j.echecs.map((e: { adresse: string; motif: string }) => `${e.adresse} (${e.motif})`).join(' ; ')}.` : '',
      j?.repli ? 'Adresse de repli utilisée — aucune adresse « qualité » n’est déclarée.' : '',
      j?.pdfJoint === false ? 'Le PDF n’a pas pu être joint : le corps du message porte le tableau.' : '',
    ].filter(Boolean);
    showToast(parts.join(' '), j?.ok ? 'info' : 'erreur');
    setTour((t) => t + 1);
  });

  /* Même règle que le formulaire : le panneau s'ouvre en haut de page alors que
     la ligne cliquée est plus bas — on l'amène à l'écran. */
  const racine = useRef<HTMLDivElement>(null);
  useEffect(() => { racine.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [ligne.documentId]);

  /* La clé joint la référence et le rang : deux dossiers de la MÊME
     non-conformité partageraient sinon une clé, et React réutiliserait la
     mauvaise ligne au changement de mois. */
  const lignesReleve = useMemo(() => (r?.lignes || []).map((l, i) => ({ ...l, cle: `${l.ncRef}-${i}` })), [r]);

  return (
    <div ref={racine} style={{ ...card, padding: 18, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          {ligne.nom}{r ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> — {r.periode.libelle}</span> : null}
        </h2>
        <span style={{ flex: 1 }} />
        {onFerme && <Bouton taille="sm" onClick={onFerme}>Tout le laboratoire</Bouton>}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'grid', gap: 5 }}>
          <span style={lbl}><Help aide={AIDE_PERIODE}>Ce qu’on adresse</Help></span>
          <Segment ariaLabel="Ce qu’on adresse" valeur={selection ? 'selection' : 'periode'} onChange={(v) => setSelection(v === 'selection')}
            options={[
              { valeur: 'periode', libelle: 'Toute la période' },
              { valeur: 'selection', libelle: 'Une sélection', compteur: choix.length || undefined },
            ]} />
        </div>
        <span style={{ flex: 1 }} />
        {peutAdresser && (
          <Bouton taille="sm" variante="primaire" chargement={estOccupe('pdf')}
            disabled={estOccupe() || chargement || plageInvalide || selectionVide || desactive}
            title={desactive ? empechement : selectionVide ? 'Cochez au moins une non-conformité.' : plageInvalide ? 'La période est incomplète ou inversée.' : undefined}
            onClick={() => void telecharger()}>
            <I n="download" s={13} />Télécharger le PDF
          </Bouton>
        )}
        <Bouton taille="sm" disabled={estOccupe() || chargement || plageInvalide || selectionVide}
          onClick={() => {
            window.location.href = `/api/ged/ged/correspondants/${ligne.documentId}/releve.csv?${requete}`;
          }}>
          Exporter (CSV)
        </Bouton>
        {peutAdresser && (
          <Bouton taille="sm" chargement={estOccupe('mail')}
            disabled={estOccupe() || chargement || !!empechement || plageInvalide || selectionVide}
            title={empechement || (selectionVide ? 'Cochez au moins une non-conformité.' : `Envoyer à ${dest.adresses.join(', ')}`)}
            onClick={() => void envoyer()}>
            <I n="mail" s={13} />Envoyer par courriel
          </Bouton>
        )}
      </div>

      {plageInvalide && <div style={encart}>La période choisie en haut de l’onglet est incomplète ou inversée.</div>}

      {mode === 'selection' && d && !chargement && (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
            <span style={lbl}>Non-conformités imputées à {ligne.nom}</span>
            <span style={{ flex: 1 }} />
            {d.imputees.length > 0 && (
              <Bouton taille="sm" variante="discret"
                onClick={() => setChoix(choix.length === d.imputees.length ? [] : d.imputees.map((n) => n.ref))}>
                {choix.length === d.imputees.length ? 'Tout décocher' : 'Tout cocher'}
              </Bouton>
            )}
          </div>
          {d.imputees.length === 0 ? (
            <EmptyState titre="Aucune non-conformité ne lui est imputée."
              indice="Une non-conformité s’impute à un correspondant dans sa déclaration (« L’erreur vient-elle d’un correspondant extérieur ? »). Seules les NC retenues figurent au relevé." />
          ) : d.imputees.map((n) => {
            const coche = choix.includes(n.ref);
            return (
              <label key={n.ref} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer',
                padding: '6px 8px', borderRadius: 'var(--radius-sm)',
                background: coche ? 'var(--cyan-50)' : 'transparent',
              }}>
                <input type="checkbox" checked={coche} style={{ marginTop: 3 }}
                  onChange={(e) => setChoix(e.target.checked ? [...choix, n.ref] : choix.filter((x) => x !== n.ref))} />
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, whiteSpace: 'nowrap' }}>{n.ref}</span>
                <span style={{ flex: 1 }}>{n.titre || <em style={{ color: 'var(--text-muted)' }}>sans intitulé</em>}</span>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {fmtDate(n.date)} · {n.dossiers ? `${n.dossiers} dossier(s)` : 'non détaillée'}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* ⚠️ L'AVERTISSEMENT SE LIT AVANT LE CLIC. La confirmation du second
          envoi arrive de toute façon au moment du geste, mais la découvrir là
          est trop tard pour changer de période : on vient de choisir un mois
          déjà adressé sans le savoir. */}
      {d?.dejaAdresse?.existe && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>Déjà adressé.</strong> {d.dejaAdresse.phrase}{' '}
          En produire un second reste possible — il vous sera demandé de le confirmer.
        </div>
      )}
      {empechement && <div style={encart}>{empechement}</div>}
      {dest.repli && !empechement && (
        <div style={encart}>
          Aucune adresse « relevés et signalements qualité » n’est déclarée : l’envoi partirait à
          l’adresse <strong>{dest.adresses.join(', ')}</strong>, qui n’est pas celle de la personne qui
          répond d’un écart.
        </div>
      )}

      {chargement && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Calcul du relevé…</p>}
      {echec && <div style={encart}>{echec}</div>}

      {r && !chargement && (<>
        {r.stats ? <StatsDuReleve r={r} /> : (
        <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
          <strong>{d?.titre}</strong> — {r.compte.ncs} non-conformité(s) · {r.compte.dossiers} dossier(s) concerné(s)
          {r.compte.moisPrecedents.length > 0 && (
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}· rappel : {r.compte.moisPrecedents.map((m) => `${m.mois} — ${m.dossiers}`).join(' · ')}
            </span>
          )}
        </div>)}
        {r.stats && <div style={{ ...lbl, marginTop: 4 }}>Détail des non-conformités</div>}

        {/* En mode « sélection », un relevé vide ne dit rien : la liste à cocher
            est juste au-dessus. Sinon, le tableau porte son état vide. */}
        {r.lignes.length === 0 && mode === 'selection' ? null : (
          /* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle. L'export reste
             celui du SERVEUR (« Exporter (CSV) », avec l'en-tête du relevé) : un
             seul export par registre. */
          <Tableau
            id="correspondants-releve"
            ajusterLargeur
            niveau="simple"
            lignes={lignesReleve}
            cleLigne={(l) => l.cle}
            vide={{
              titre: d?.phraseVide || 'Aucune non-conformité ne lui est imputée sur cette période.',
              indice: 'Ce n’est pas une erreur : c’est un résultat, et le document le dira en toutes lettres si vous l’adressez.',
            }}
            colonnes={[
              { cle: 'date', libelle: 'Date', type: 'date', valeur: (l) => l.date, rendu: (l) => fmtDate(l.date) },
              { cle: 'ncRef', libelle: 'Référence', valeur: (l) => l.ncRef, style: { fontFamily: 'var(--font-mono)', fontWeight: 700 }, rendu: (l) => l.ncRef },
              { cle: 'ncTitre', libelle: 'Constat', valeur: (l) => l.ncTitre, rendu: (l) => l.ncTitre },
              { cle: 'dossier', libelle: 'Dossier concerné', valeur: (l) => l.dossier || '', rendu: (l) => l.dossier || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>non détaillé</span> },
              { cle: 'gravite', libelle: 'Gravité', type: 'liste', valeur: (l) => l.gravite, rendu: (l) => l.gravite },
              { cle: 'statut', libelle: 'État', type: 'liste', valeur: (l) => l.statut, rendu: (l) => l.statut },
              { cle: 'suites', libelle: 'Suites', valeur: (l) => l.suites || '', rendu: (l) => l.suites || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>en cours</span> },
            ]}
          />
        )}

        {(d?.anterieurs?.length ?? 0) > 0 && (
          <div style={{ display: 'grid', gap: 5 }}>
            <span style={lbl}>Relevés déjà produits</span>
            {d?.anterieurs.map((a) => (
              <div key={a.ref} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{a.ref}</span>
                {' — '}{a.periode} · {a.dossiers ?? 0} dossier(s) ·{' '}
                {a.envoye
                  ? <>envoyé le {fmtDate(a.dateEnvoi)} à {a.destinataires.join(', ')}</>
                  : <>PDF produit le {fmtDate(a.datePrepare)} — <span style={{ color: 'var(--text-muted)' }}>remis par le laboratoire, hors plateforme</span></>}
              </div>
            ))}
          </div>
        )}
      </>)}
      {confirmation}
    </div>
  );
}

/**
 * La rubrique « Relevé des NC » d'une fiche de l'annuaire : ce qui est imputé à ce contact et ses adresses
 * « qualité ». Statistiques et relevé se préparent dans les NC, onglet « Statistiques » (20/10/2026).
 * Un compte qui ne voit pas les NC ne voit pas cette rubrique (lecture refusée → rien).
 */
export function ReleveDuContact({ id }: { id: string }) {
  const { naviguerVers } = useGed();
  const [etat, setEtat] = useState<{ ligne: Ligne | null; aujourdhui: string; peutReleve: boolean } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ferme, setFerme] = useState(false);
  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/correspondants?entite=${encodeURIComponent(id)}`, { credentials: 'include' })
      .then((r) => { if (r.status === 403) { if (vivant) setFerme(true); return Promise.reject(new Error('fermé')); } return r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)); })
      .then((j: { lignes: Ligne[]; aujourdhui: string; peutReleve: boolean }) => { if (vivant) setEtat({ ligne: j.lignes[0] || null, aujourdhui: j.aujourdhui, peutReleve: j.peutReleve }); })
      .catch((e: Error) => { if (vivant && e.message !== 'fermé') setErreur(e.message); });
    return () => { vivant = false; };
  }, [id]);
  if (ferme) return <p style={{ margin: 0, color: 'var(--text-muted)' }}>Le relevé des NC se consulte dans les non-conformités.</p>;
  if (erreur) return <FieldError>Le relevé n’a pas pu être lu : {erreur}</FieldError>;
  if (!etat) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Lecture…</div>;
  const l = etat.ligne;
  if (!l) return <p style={{ margin: 0, color: 'var(--text-muted)' }}>Aucune non-conformité ni réclamation n’est rattachée à ce contact.</p>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Pastille ton={l.compte.ncs ? 'warning' : 'neutral'}>{l.compte.ncs} NC imputée(s)</Pastille>
        <Pastille ton="neutral">{l.compte.dossiers} dossier(s)</Pastille>
        {(l.compte.signalees ?? 0) > 0 && <Pastille ton="info">{l.compte.signalees} signalée(s) par lui</Pastille>}
        {(l.compte.reclamations ?? 0) > 0 && <Pastille ton="info">{l.compte.reclamations} réclamation(s)</Pastille>}
      </div>
      <div style={{ fontSize: 13 }}>
        {l.destinatairesQualite.adresses.length
          ? <>Le relevé partira à <strong>{l.destinatairesQualite.adresses.join(', ')}</strong>{l.destinatairesQualite.repli ? ` (adresse « ${l.destinatairesQualite.usage} », faute d’adresse « qualité »)` : ''}.</>
          : <span style={{ color: 'var(--amber-700)' }}>Aucune adresse email : le relevé pourra être téléchargé en PDF, pas envoyé. Ajoutez une adresse d’usage « Qualité » à la fiche.</span>}
      </div>
      {etat.peutReleve && <div><Bouton taille="sm" variante="primaire" onClick={() => {
        naviguerVers('statsnc');
        /* `naviguerVers` pose l'adresse ; le contact passe dans la requête, que l'onglet lit en s'ouvrant. */
        const url = new URL(window.location.href); url.searchParams.set('contact', l.code); window.history.replaceState(window.history.state, '', url);
      }}>Ses statistiques et son relevé (NC, onglet « Statistiques »)</Bouton></div>}
    </div>
  );
}
